import { CheckCircle, Pause, Play } from "@phosphor-icons/react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import Loading from "./Loading";
import useUser from "@/lib/queries/user/useUser";
import { useStytchSession } from "@stytch/react";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";
import useSubscription from "@/lib/queries/user/useSubscription";
import { useState } from "react";
import CouponCode from "./billing/CouponCode";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

function SubscriptionOffer() {
  const { t } = useTranslation("settings");
  const stytchSession = useStytchSession();
  const {
    usePauseSubscription,
    useUnpauseSubscription,
    useCancelSubscription,
  } = useSubscription();
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [isUnpauseModalOpen, setIsUnpauseModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const { data: user, isLoading: isUserLoading } = useUser({
    enabled: stytchSession?.session !== null,
  });

  const { mutate: pause, isPending: isPausePending } = usePauseSubscription();
  const { mutate: unpause, isPending: isUnpausePending } =
    useUnpauseSubscription();
  const { mutate: cancel, isPending: isCancelPending } =
    useCancelSubscription();

  if (isUserLoading) {
    return <Loading />;
  }
  function getCurrencySymbol(
    currency: string | undefined
  ): import("react").ReactNode {
    if (!currency) return "$"; // Default to USD symbol if currency is undefined

    switch (currency.toUpperCase()) {
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      case "JPY":
        return "¥";
      case "CNY":
        return "¥";
      case "KRW":
        return "₩";
      case "INR":
        return "₹";
      case "RUB":
        return "₽";
      case "BRL":
        return "R$";
      case "CAD":
        return "C$";
      case "AUD":
        return "A$";
      default:
        return currency; // If not recognized, just show the currency code
    }
  }

  const subscriptionCost = (user?.stripeCustomer?.price || 0) / 100;

  const handlePauseConfirm = () => {
    pause();
    setIsPauseModalOpen(false);
  };

  const handleUnpauseConfirm = () => {
    unpause();
    setIsUnpauseModalOpen(false);
  };

  const handleCancelConfirm = () => {
    cancel();
    setIsCancelModalOpen(false);
  };

  const SubscriptionPriceDisplay = () => {
    if (isNaN(subscriptionCost) || subscriptionCost < 0) {
      return <span className="text-red-500">{t("offer.price.invalid")}</span>;
    }

    let interval = user?.stripeCustomer?.interval;

    return (
      <h1 className="text-5xl">
        {getCurrencySymbol(user?.stripeCustomer?.currency ?? "gbp")}{" "}
        {subscriptionCost.toFixed(2)}
        <span className="text-2xl text-muted-foreground">/ {interval}</span>
      </h1>
    );
  };

  return (
    <Card
      className={cn(
        "bg-white border w-fit border-primary",
        user?.paused && "border-warning"
      )}
    >
      {user?.stripeCustomer?.coupon && (
        <CouponCode
          code={user?.stripeCustomer?.coupon || "default"}
          amount={user?.stripeCustomer?.discountPercent || 0}
          className="relative float-right ml-2 -top-8 -right-6"
        />
      )}
      <CardHeader className="flex flex-row items-center justify-start gap-4">
        <CardTitle>
          <SubscriptionPriceDisplay />
        </CardTitle>
        {user?.subscribed && (
          <CardDescription
            className={cn(
              "flex flex-row p-3 text-base rounded-lg text-foreground",
              user.paused
                ? "bg-warning text-warning-foreground"
                : "bg-secondary"
            )}
          >
            {user.paused ? (
              <>
                <Pause size={24} weight="fill" className="mr-1" />
                {t("offer.status.paused")}
              </>
            ) : (
              <>
                <CheckCircle size={24} weight="bold" className="mr-1" />
                {t("offer.status.active")}
              </>
            )}
          </CardDescription>
        )}
      </CardHeader>

      <CardContent>
        <h2 className="text-2xl font-semibold text-foreground">
          {t("offer.title")}
        </h2>
        <p className="mt-5 text-lg text-foreground">
          {!user?.paused && user?.subscribed && t("offer.description.active")}
          {user?.paused && user?.subscribed && t("offer.description.paused")}
        </p>
        <div
          className={cn(
            "p-5 mt-5 text-2xl rounded-lg bg-secondary text-foreground",
            user?.paused ? "bg-warning text-warning-foreground" : "bg-secondary"
          )}
        >
          {user?.paused
            ? t("offer.features.paused")
            : t("offer.features.unlimited")}
        </div>
      </CardContent>

      <CardFooter className="flex flex-col gap-2">
        {user?.paused ? (
          <>
            <Dialog
              open={isUnpauseModalOpen}
              onOpenChange={setIsUnpauseModalOpen}
            >
              <DialogTrigger asChild>
                <Button
                  variant="secondary"
                  className="w-full font-bold"
                  disabled={isUnpausePending}
                >
                  <Play weight="fill" className="w-6 h-6 mr-2 font-bold" />
                  {t("offer.unpause.button")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{t("offer.unpause.title")}</DialogTitle>
                  <DialogDescription className="space-y-3 text-left">
                    <p>{t("offer.unpause.description.line1")}</p>
                    <p>
                      <strong>{t("offer.unpause.description.line2")}</strong>
                    </p>
                    <p>{t("offer.unpause.description.line3")}</p>
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsUnpauseModalOpen(false)}
                  >
                    {t("offer.unpause.cancel")}
                  </Button>
                  <Button
                    onClick={handleUnpauseConfirm}
                    isLoading={isUnpausePending}
                  >
                    <Play weight="fill" className="w-4 h-4 mr-2" />
                    {t("offer.unpause.confirm")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        ) : (
          <div className="flex flex-row gap-2">
            <Dialog open={isPauseModalOpen} onOpenChange={setIsPauseModalOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full font-bold"
                  disabled={isPausePending}
                >
                  <Pause weight="fill" className="w-6 h-6 mr-2 font-bold" />
                  {t("offer.pause.button")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{t("offer.pause.title")}</DialogTitle>
                  <DialogDescription className="space-y-3 text-left">
                    <p>{t("offer.pause.description.line1")}</p>
                    <p>{t("offer.pause.description.line2")}</p>
                    <p>{t("offer.pause.description.line3")}</p>
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsPauseModalOpen(false)}
                  >
                    {t("offer.pause.cancel")}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handlePauseConfirm}
                    isLoading={isPausePending}
                  >
                    <Pause weight="fill" className="w-4 h-4 mr-2" />
                    {t("offer.pause.confirm")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog
              open={isCancelModalOpen}
              onOpenChange={setIsCancelModalOpen}
            >
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full font-bold underline text-muted-foreground"
                  disabled={isCancelPending}
                >
                  {t("offer.cancel.button")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{t("offer.cancel.title")}</DialogTitle>
                  <DialogDescription className="space-y-3 text-left">
                    <p>{t("offer.cancel.description.line1")}</p>
                    <p>
                      <strong>{t("offer.cancel.description.line2")}</strong>
                    </p>
                    <p>
                      <strong>{t("offer.cancel.description.line3")}</strong>
                    </p>
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsCancelModalOpen(false)}
                  >
                    {t("offer.cancel.keep")}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleCancelConfirm}
                    isLoading={isCancelPending}
                  >
                    {t("offer.cancel.confirm")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
        <span>
          {t("offer.billing.portal")}{" "}
          <Link
            to="https://billing.arcology.io/p/login/5kQ4gB8Mbg84fy326X3Ru00"
            target="_blank"
            className="underline text-primary"
          >
            {t("offer.billing.link")}
          </Link>
        </span>
      </CardFooter>
    </Card>
  );
}

export default SubscriptionOffer;
